import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
    Autocomplete,
    Button,
    Checkbox,
    Icon,
    Progress,
    TextField,
    Textarea,
    Typography,
} from '@equinor/eds-core-react'
import {
    AreaDto,
    DeviceDto,
    PlantDto,
    ProjectDto,
    StidTagRequestModel,
    TagDataDto,
    ThumbnailDto,
    UpdateSensorsDataDto,
} from '@/core/models/types'
import { baseFont } from '@/styles/layout'
import { external_link, help_outline, error_outlined, info_circle, image_add, camera } from '@equinor/eds-icons'
import { useAtom } from 'jotai'
import {
    addGlobalErrorMessage,
    addSnackBarMessage,
    onboardedEditedAtom,
    showErrorEditOnboardedAtom,
} from '@/core/store/globalStore'
import { COORDINATES_REGEX, normalizeCoordinates } from '../../hooks/useOnboardingStepper'
import {
    useGetAreasService,
    useGetPlants,
    useGetProjectsService,
} from '@/core/hooks-and-services/onboardedSensorsService'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { Sensor } from '../../models/sensor'
import { getImage, updateSensor } from '@/api/apiService'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/core/models/enum'
import { mapInstalledSensorToSensor } from '@/core/hooks-and-services/onboardedSensorsService'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { Pagination } from '@equinor/eds-core-react'
import EditImage from '../EditOnboarded/EditImage'
import { FileHolderDto, FileResultEnum } from '@/api_generated/api'
import { VContainer } from '@/styles'
import { useInfoDialog } from '@/hooks'
import TakePhoto from './TakePhoto'
import SelectImageFromLibrary from './SelectImageFromLibrary'
import { useEditMovedDialog } from './useEditMovedDialog'
import PopoverDialog from '@/components/popover/PopoverDialog'

interface EditOnboardedProps {
    sensor: Sensor | null
    setSensor: (sensor: Sensor | null) => void
    sensorAdditionalData: TagDataDto | null
    handleEditOnboardedBackButton: () => void
    hideOptionsMenu: () => void
}

export const missingPlant = 'Please select a plant.'
export const missingProject = 'Please select a project.'
export const missingDevice = "Please select a device. If you're unsure, check the portfolio for images and details."
const missingVendorSerialNumber =
    'Please enter the vendor serial number. You can find the number on the sensor or by scanning the QR code.'
const missingSuperiorTag = 'Missing superior tag. You can find the superior tag in STID.'
const missingCoordinates = 'Missing coordinates. You can find the coordinates in Echo.'
const tooltipSuperiorTag = 'You can find the superior tag to be monitored in STID or by physical tag.'
const wrongCoordinates = 'Incorrect coordinates. Ensure you have found the correct coordinates in Echo.'
const tooltipVendorSerialNumber =
    'You can find the vendor serial number on the sensor. To quickly add it you can scan the QR code on the sensor or type it in the input field.'
const tooltipDescription = 'Add extra information for the sensor.'
const missingArea = 'Please select area.'
const tooltipArea =
    'Area will be automatically filled if the superior tag has an area linked to it. You can manually change the area afterwards if needed.'

/**
 * Keeps a selection in sync with the fallback derived from asynchronously loaded options
 * until the user picks or clears a value (a plain useState would freeze the value the
 * initializer saw on the first render, before the queries resolved).
 */
function useSelection<T>(fallback: T | null) {
    const [override, setOverride] = useState<T | null | undefined>(undefined)
    return [override === undefined ? fallback : override, setOverride] as const
}

const EditOnboarded: React.FC<EditOnboardedProps> = ({
    sensor,
    setSensor,
    sensorAdditionalData,
    handleEditOnboardedBackButton,
    hideOptionsMenu,
}) => {
    const [showErrorEditOnboarded, setShowErrorEditOnboarded] = useAtom(showErrorEditOnboardedAtom)
    const [isTooltipVendorSerialNumberOpen, setTooltipVendorSerialNumberIsOpen] = useState(false)
    const [isTooltipSuperiorTagOpen, setTooltipSuperiorTagIsOpen] = useState(false)
    const [isTooltipDescriptionOpen, setTooltipDescriptionIsOpen] = useState(false)
    const [isTooltipAreaOpen, setTooltipAreaIsOpen] = useState(false)
    const [onboardedEdited, setOnboardedEdited] = useAtom(onboardedEditedAtom)

    const vendorSerialNumberRef = useRef<HTMLDivElement>(null)
    const superiorTagRef = useRef<HTMLDivElement>(null)
    const descriptionRef = useRef<HTMLDivElement>(null)
    const areaRef = useRef<HTMLDivElement>(null)

    const { data: projects = [] } = useGetProjectsService(sensor?.plant || '')
    const { data: areas = [] } = useGetAreasService(sensor?.plant || '')
    const { data: plants, error, isLoading } = useGetPlants()
    const { data: devices } = useGetDevices()

    const queryClient = useQueryClient()

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onConfirm: () => {},
        onReject: () => {
            setOnboardedEdited(false)
            handleEditOnboardedBackButton()
        },
        autoClose: true,
    })

    const { showConfirm: showConfirmMoved } = useEditMovedDialog({
        title: 'Sensor location changed',
        content1: 'You have changed superior tag and/or coordinates.',
        content2: 'Would you like to create a new tag and time series?',
        bullets: [
            'If the sensor was moved, it should be generated a new tag and new timeseries.',
            'If you only corrected info, the tag and time series should remain the same.',
        ],
        rejectButtonTitle: 'Create new',
        confirmButtonTitle: 'Keep existing',
        onReject: () => {
            const base = pendingPayloadRef.current
            if (!base) return
            doSave({ ...base, sensorIsPhysicallyMoved: true })
        },
        onConfirm: () => {
            const base = pendingPayloadRef.current
            if (!base) return
            doSave({ ...base, sensorIsPhysicallyMoved: false })
        },
        onClose: () => {},
        autoClose: true,
    })

    const pendingPayloadRef = useRef<UpdateSensorsDataDto | null>(null)

    const buildPayload = (moved: boolean): UpdateSensorsDataDto => ({
        plant: selectedPlant?.shortName,
        description: selectedDescription,
        coordinatesNotAvailable: !!selectedCoordinatesNotAvailable,
        coordinates: selectedCoordinates,
        project: selectedProject?.projectCode,
        superiorTag: selectedSuperiorTag,
        area: selectedArea?.areaCode,
        sensorModel: selectedDevice?.key,
        vendorSerialNumber: normalizeVendorSerialNumber(selectedVendorSerialNumber),
        sensorIsPhysicallyMoved: moved,
    })

    const [selectedPlant, setSelectedPlant] = useSelection<PlantDto>(
        plants?.find(
            (plant) =>
                plant.shortName === sensorAdditionalData?.instCode && plant.plantId === sensorAdditionalData?.plantId
        ) ?? null
    )

    const [selectedProject, setSelectedProject] = useSelection<ProjectDto>(
        projects?.find((project) => project.projectCode === sensorAdditionalData?.projectCode) ?? null
    )
    const [selectedDevice, setSelectedDevice] = useSelection<DeviceDto>(
        devices?.find((device) => device.key === sensor?.sensorModel) ?? null
    )

    // Keep the sensor's current model listed even if it is no longer selectable for new onboardings.
    const selectableDevices = useMemo(
        () => (devices ?? []).filter((device) => device.selectable || device.key === sensor?.sensorModel),
        [devices, sensor?.sensorModel]
    )

    const [selectedVendorSerialNumber, setSelectedVendorSerialNumber] = useState<string | null>(
        sensor?.vendorSerialNumber ? sensor?.vendorSerialNumber : null
    )

    const normalizeVendorSerialNumber = (value: string | null) => {
        if (value === null) return null
        const normalized = value.trim().toUpperCase()
        return normalized === '' ? null : normalized
    }

    const formatCoordinates = (sensorAdditionalData: StidTagRequestModel | null) => {
        const coords = sensorAdditionalData?.coordinates?.[0]

        const result = coords
            ? `E${coords?.xCoordinate ?? 0}mm,` + `N${coords?.yCoordinate ?? 0}mm,` + `U${coords?.zCoordinate ?? 0}mm`
            : null
        return result
    }
    const [selectedCoordinates, setSelectedCoordinates] = useState<string | null>(
        formatCoordinates(sensorAdditionalData)
    )

    const [selectedSuperiorTag, setSelectedSuperiorTag] = useState<string | null>(
        sensorAdditionalData?.superiorTag ? sensorAdditionalData?.superiorTag : null
    )

    const [selectedArea, setSelectedArea] = useSelection<AreaDto>(
        areas.find((area) => area.areaCode === sensorAdditionalData?.locationCode) ?? null
    )

    const [selectedCoordinatesNotAvailable, setSelectedCoordinatesNotAvailable] = useState<boolean | null>(
        sensor?.coordinatesNotAvailable ? sensor?.coordinatesNotAvailable : false
    )

    const [selectedDescription, setSelectedDescription] = useState<string | null>(
        sensor?.userAddedDescription ? sensor?.userAddedDescription : null
    )

    const [isSaving, setIsSaving] = useState<boolean>(false)

    const shouldCoordinatesBeMandatory = () => {
        return !selectedCoordinatesNotAvailable && (selectedCoordinates === '' || selectedCoordinates === null)
    }

    const isCoordinatesEmpty = selectedCoordinates === '' || selectedCoordinates === null
    const isCoordinatesValid = COORDINATES_REGEX.test(selectedCoordinates ? selectedCoordinates : '')
    const coordinatesIsNotValid = showErrorEditOnboarded && shouldCoordinatesBeMandatory()

    const files = useMemo(() => {
        return sensorAdditionalData?.files || []
    }, [sensorAdditionalData])

    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [itemsPerPage] = useState(3)
    const [editImageOpen, setEditImageOpen] = useState(false)
    const [selectedFile, setSelectedFile] = useState<ThumbnailDto | null>(null)
    const [selectedImage, setSelectedImage] = useState<FileHolderDto | null>(null)

    const [showCamera, setShowCamera] = useState(false)
    const [showSelectFromLibrary, setShowSelectFromLibrary] = useState(false)

    const { showInfo } = useInfoDialog(
        {
            title: 'Access Required',
            content: (
                <VContainer $gap="16px">
                    <Typography>
                        You do not have permission to view this image. Please request the role “STID Read Internal” for
                        relevant plant in AccessIT to get access.
                    </Typography>
                </VContainer>
            ),
            type: 'Info',
        },
        []
    )

    const editImage = async (image: ThumbnailDto) => {
        if (!selectedPlant || !selectedPlant.shortName) return
        if (!image || !image.id) return
        setSelectedFile(image)
        try {
            const data = await getImage(selectedPlant.shortName, image.id)
            if (data.result !== FileResultEnum.Success) {
                showInfo()
                return
            }
            setSelectedImage(data)
            setSelectedFile(image)
            setEditImageOpen(true)
        } catch (error) {
            console.error('Error updating sensor:', error)
            addSnackBarMessage(`Failed to get image to edit`)
        }
    }

    const paginatedData = useMemo(() => {
        if (!files) return null
        const startIndex = (currentPageIndex - 1) * itemsPerPage
        return files.slice(startIndex, startIndex + itemsPerPage)
    }, [files, currentPageIndex, itemsPerPage])

    const ThumbnailsList: React.FC<{ images: ThumbnailDto[] | null }> = ({ images }) => {
        if (!images || images.length === 0) return null

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-start' }}>
                {images.map((image, index) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Thumbnail
                            src={image.thumbnail || ''}
                            alt={`Thumbnail ${index}`}
                            onClick={() => editImage(image)}
                        />
                        <Button as="button" onClick={() => editImage(image)} variant="outlined" disabled={isSaving}>
                            <Label>Edit image</Label>
                        </Button>
                    </div>
                ))}
            </div>
        )
    }

    const handleSelectionChange = (
        type: 'plant' | 'project' | 'area' | 'device',
        changes: { selectedItems: unknown[] }
    ) => {
        const selectedItem = changes.selectedItems[0] ?? null
        if (selectedItem) {
            if (type === 'plant') {
                setSelectedPlant(selectedItem as PlantDto)
                setShowErrorEditOnboarded(false)
            }
            if (type === 'project') {
                setSelectedProject(selectedItem as ProjectDto)
                setShowErrorEditOnboarded(false)
            }
            if (type === 'area') {
                setSelectedArea(selectedItem as AreaDto)
                setShowErrorEditOnboarded(false)
            }
            if (type === 'device') {
                setSelectedDevice(selectedItem as DeviceDto)
                setShowErrorEditOnboarded(false)
            }
            setOnboardedEdited(true)
        }
    }

    const handleTextChange = (
        type: 'superiortag' | 'coordinates' | 'description' | 'vendorserialnumber',
        text: string
    ) => {
        if (type === 'superiortag') {
            setSelectedSuperiorTag(text)
            setShowErrorEditOnboarded(false)
        }
        if (type === 'vendorserialnumber') {
            setSelectedVendorSerialNumber(text)
            setShowErrorEditOnboarded(false)
        }
        if (type === 'coordinates') {
            setSelectedCoordinates(text)
            setShowErrorEditOnboarded(false)
        }
        if (type === 'description') {
            setSelectedDescription(text)
            setShowErrorEditOnboarded(false)
        }
        setOnboardedEdited(true)
    }

    const handleCheckedChange = (type: 'coordinatesnotavailable', checked: boolean) => {
        if (type === 'coordinatesnotavailable') {
            setSelectedCoordinatesNotAvailable(checked)
            setShowErrorEditOnboarded(false)
        }
        setOnboardedEdited(true)
    }

    const handleClear = (
        type:
            | 'plant'
            | 'project'
            | 'superiortag'
            | 'coordinates'
            | 'coordinatesnotavailable'
            | 'description'
            | 'area'
            | 'device'
    ) => {
        switch (type) {
            case 'plant':
                setSelectedPlant(null)
                break
            case 'project':
                setSelectedProject(null)
                break
            case 'superiortag':
                setSelectedSuperiorTag(null)
                break
            case 'coordinates':
                setSelectedCoordinates(null)
                break
            case 'coordinatesnotavailable':
                setSelectedCoordinatesNotAvailable(null)
                break
            case 'description':
                setSelectedDescription(null)
                break
            case 'area':
                setSelectedArea(null)
                break
            case 'device':
                setSelectedDevice(null)
                break
        }
    }

    const handleChangeSuperiorTag = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        handleTextChange('superiortag', value)
    }

    const handleChangeCoordinates = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        handleTextChange('coordinates', normalizeCoordinates(value))
    }

    const handleChangeVendorSerialNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        handleTextChange('vendorserialnumber', value)
    }

    const handleCoordinatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target
        handleCheckedChange('coordinatesnotavailable', checked)
    }

    const handleChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target
        handleTextChange('description', value)
    }

    const firstStepComplete = () => {
        const isAvailable = selectedPlant && selectedDevice && selectedProject
        return Boolean(isAvailable)
    }

    const handleCancelButton = async () => {
        if (onboardedEdited) {
            showConfirm()
        } else {
            handleEditOnboardedBackButton()
        }
    }

    const doSave = async (payload: UpdateSensorsDataDto) => {
        if (!sensor?.sensorDbKey || !sensor?.plant) {
            return
        }
        setIsSaving(true)
        const response = await updateSensor(sensor?.sensorDbKey, sensor?.plant, payload)
        if (response.success) {
            setOnboardedEdited(false)
            addSnackBarMessage(sensor?.tag + ` successfully updated`)
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.OnboardedSensors],
            })
            setSensor(response.updatedSensor === undefined ? null : mapInstalledSensorToSensor(response.updatedSensor))
            hideOptionsMenu()
        } else {
            const errorMessage = `Failed to update sensor data for ` + sensor?.tag
            addSnackBarMessage(errorMessage)
            addGlobalErrorMessage(errorMessage)
        }
        setIsSaving(false)
    }

    const handleEditOnboardedSaveButton = async () => {
        try {
            if (!sensor?.sensorDbKey || !sensor?.plant) {
                return
            }
            if (!firstStepComplete()) {
                setShowErrorEditOnboarded(true)
                return
            }
            const isCoordinatesEmpty = selectedCoordinates === '' || selectedCoordinates === null
            if (!selectedCoordinatesNotAvailable && isCoordinatesEmpty) {
                setShowErrorEditOnboarded(true)
                return
            }
            if (selectedCoordinates && !COORDINATES_REGEX.test(selectedCoordinates)) {
                setShowErrorEditOnboarded(true)
                return
            }

            if (!selectedSuperiorTag || !selectedArea) {
                setShowErrorEditOnboarded(true)
                return
            }

            if (!normalizeVendorSerialNumber(selectedVendorSerialNumber)) {
                setShowErrorEditOnboarded(true)
                return
            }
            pendingPayloadRef.current = buildPayload(false)

            console.log('Pending payload:', pendingPayloadRef.current)
            if (
                formatCoordinates(sensorAdditionalData) != selectedCoordinates ||
                sensorAdditionalData?.superiorTag !== selectedSuperiorTag
            ) {
                showConfirmMoved()
            } else {
                const base = pendingPayloadRef.current
                if (!base) return
                doSave({ ...base, sensorIsPhysicallyMoved: false })
            }
        } catch (error) {
            setIsSaving(false)
            console.error('Error updating sensor:', error)
            addSnackBarMessage(`Failed to update sensor data`)
        }
    }

    return (
        <>
            <StyledContainer>
                <StyledAutocomplete
                    label={
                        <RowStyle>
                            <InputStyledLabel>Plant</InputStyledLabel>
                        </RowStyle>
                    }
                    meta={
                        <RowStyle>
                            <InputStyledLabel>Cannot be changed</InputStyledLabel>
                        </RowStyle>
                    }
                    options={plants}
                    optionLabel={(plant: unknown) => (plant as PlantDto).description ?? ''}
                    selectedOptions={selectedPlant ? [selectedPlant] : []}
                    onOptionsChange={(changes) =>
                        handleSelectionChange('plant', {
                            selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                        })
                    }
                    helperText={showErrorEditOnboarded && !selectedPlant ? missingPlant : ''}
                    helperIcon={
                        showErrorEditOnboarded && !selectedPlant ? (
                            <Icon data={error_outlined} title="Error" size={16} />
                        ) : null
                    }
                    variant={showErrorEditOnboarded && !selectedPlant ? 'error' : undefined}
                    onClear={() => handleClear('plant')}
                    disabled={true}
                />
                <StyledAutocomplete
                    label={
                        <RowStyle>
                            <InputStyledLabel>Project</InputStyledLabel>
                        </RowStyle>
                    }
                    meta={
                        <RowStyle>
                            <InputStyledLabel>Mandatory</InputStyledLabel>
                        </RowStyle>
                    }
                    options={projects}
                    optionLabel={(project: unknown) => (project as ProjectDto).projectCode ?? 'No Project Code'}
                    selectedOptions={selectedProject ? [selectedProject] : []}
                    onOptionsChange={(changes) => {
                        handleSelectionChange('project', {
                            selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                        })
                    }}
                    onClear={() => handleClear('project')}
                    disabled={isSaving}
                />
                {showErrorEditOnboarded && !selectedProject && (
                    <RowStyleWithMany>
                        <RowStyle style={{ alignItems: 'flex-start' }}>
                            {' '}
                            {/* Align items to the top */}
                            <StyledErrorIcon data={error_outlined} size={16} />
                            <StyledErrorTypography variant="body_short">
                                {!selectedProject ? missingProject : ''}
                            </StyledErrorTypography>
                        </RowStyle>
                    </RowStyleWithMany>
                )}
                <StyledAutocomplete
                    label={
                        <RowStyle>
                            <InputStyledLabel>Device:</InputStyledLabel>
                        </RowStyle>
                    }
                    meta={
                        <RowStyle>
                            <InputStyledLabel>Mandatory</InputStyledLabel>
                        </RowStyle>
                    }
                    options={selectableDevices}
                    optionLabel={(device: unknown) => (device as DeviceDto).key ?? ''}
                    selectedOptions={selectedDevice ? [selectedDevice] : []}
                    onOptionsChange={(changes) =>
                        handleSelectionChange('device', {
                            selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                        })
                    }
                    helperText={showErrorEditOnboarded && !selectedDevice ? missingDevice : ''}
                    helperIcon={
                        showErrorEditOnboarded && !selectedDevice ? (
                            <Icon data={error_outlined} title="Error" size={16} />
                        ) : null
                    }
                    variant={showErrorEditOnboarded && !selectedDevice ? 'error' : undefined}
                    onClear={() => handleClear('device')}
                    disabled={isSaving}
                />
                <StyledTextField
                    id="VendorSerialNumber"
                    label={
                        <RowStyle>
                            <InputStyledLabel style={{ whiteSpace: 'nowrap' }}>Vendor Serial Number</InputStyledLabel>
                            <div ref={vendorSerialNumberRef}>
                                <Button
                                    variant="ghost_icon"
                                    aria-label="Vendor serial number help"
                                    onClick={() => setTooltipVendorSerialNumberIsOpen(!isTooltipVendorSerialNumberOpen)}
                                >
                                    <Icon data={help_outline} size={24} />
                                </Button>
                            </div>
                        </RowStyle>
                    }
                    value={selectedVendorSerialNumber ? selectedVendorSerialNumber : ''}
                    onChange={handleChangeVendorSerialNumber}
                    required
                    helperText={
                        showErrorEditOnboarded && !normalizeVendorSerialNumber(selectedVendorSerialNumber)
                            ? missingVendorSerialNumber
                            : ''
                    }
                    helperIcon={
                        showErrorEditOnboarded && !normalizeVendorSerialNumber(selectedVendorSerialNumber) ? (
                            <Icon data={error_outlined} title="Error" size={16} />
                        ) : null
                    }
                    variant={
                        showErrorEditOnboarded && !normalizeVendorSerialNumber(selectedVendorSerialNumber)
                            ? 'error'
                            : undefined
                    }
                    meta={
                        <RowStyle>
                            <InputStyledLabel style={{ whiteSpace: 'nowrap' }}>Mandatory</InputStyledLabel>
                        </RowStyle>
                    }
                    disabled={isSaving}
                />
                <RowStyleWithMany>
                    <StyledTextField
                        id="SuperiorTag"
                        label={
                            <RowStyle>
                                <InputStyledLabel>Superior tag</InputStyledLabel>
                                <div ref={superiorTagRef}>
                                    <Button
                                        variant="ghost_icon"
                                        aria-label="save action"
                                        onClick={() => setTooltipSuperiorTagIsOpen(!isTooltipSuperiorTagOpen)}
                                    >
                                        <Icon data={help_outline} size={24}></Icon>
                                    </Button>
                                </div>
                            </RowStyle>
                        }
                        value={selectedSuperiorTag ? selectedSuperiorTag : ''}
                        onChange={handleChangeSuperiorTag}
                        variant={showErrorEditOnboarded && selectedSuperiorTag == '' ? 'error' : undefined}
                        disabled={isSaving}
                    />
                    <StyledAutocomplete
                        id="Area"
                        label={
                            <RowStyle>
                                <InputStyledLabel>Area</InputStyledLabel>
                                <div ref={areaRef}>
                                    <Button
                                        variant="ghost_icon"
                                        aria-label="save action"
                                        onClick={() => setTooltipAreaIsOpen(!isTooltipAreaOpen)}
                                    >
                                        <Icon data={info_circle} size={24}></Icon>
                                    </Button>
                                </div>
                            </RowStyle>
                        }
                        options={areas}
                        optionLabel={(area: unknown) => {
                            const { areaCode, description } = area as AreaDto
                            return areaCode && description ? `${areaCode} - ${description}` : ''
                        }}
                        selectedOptions={selectedArea ? [selectedArea] : []}
                        onOptionsChange={(changes) =>
                            handleSelectionChange('area', {
                                selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                            })
                        }
                        variant={showErrorEditOnboarded && !selectedArea ? 'error' : undefined}
                        onClear={() => handleClear('area')}
                        autoWidth={true}
                        multiline={true}
                        disabled={isSaving}
                    />
                </RowStyleWithMany>
                {showErrorEditOnboarded && (
                    <RowStyleWithMany>
                        {selectedSuperiorTag == '' && (
                            <RowStyle style={{ alignItems: 'flex-start' }}>
                                {' '}
                                {/* Align items to the top */}
                                <StyledErrorIcon data={error_outlined} size={16} />
                                <StyledErrorTypography variant="body_short">{missingSuperiorTag}</StyledErrorTypography>
                            </RowStyle>
                        )}
                        {!selectedArea && (
                            <RowStyle style={{ alignItems: 'flex-start' }}>
                                {' '}
                                {/* Align items to the top */}
                                <StyledErrorIcon data={error_outlined} size={16} />
                                <StyledErrorTypography variant="body_short">{missingArea}</StyledErrorTypography>
                            </RowStyle>
                        )}
                    </RowStyleWithMany>
                )}
                <RowStyleWithMany>
                    <StyledTextField
                        type="text"
                        id="Coordinates"
                        label={
                            <RowStyle>
                                <InputStyledLabel>Coordinates (Echo)</InputStyledLabel>
                            </RowStyle>
                        }
                        value={selectedCoordinates ? selectedCoordinates : ''}
                        onChange={handleChangeCoordinates}
                        required
                        disabled={
                            isSaving || (selectedCoordinatesNotAvailable ? selectedCoordinatesNotAvailable : false)
                        }
                    />
                    <Button
                        as="a"
                        variant="outlined"
                        disabled={isSaving}
                        href={`https://echo.equinor.com/echo3d?instCode=${selectedPlant?.shortName || ''}&tagNo=${selectedSuperiorTag || ''}`}
                        target="_blank"
                    >
                        <FormStyledLinkLabel>{'Echo'}</FormStyledLinkLabel> <Icon data={external_link} size={16} />
                    </Button>
                </RowStyleWithMany>
                {showErrorEditOnboarded && coordinatesIsNotValid && (
                    <RowStyle style={{ alignItems: 'flex-start' }}>
                        <StyledErrorIcon data={error_outlined} size={16} />
                        <StyledErrorTypography>{missingCoordinates}</StyledErrorTypography>
                    </RowStyle>
                )}
                {showErrorEditOnboarded && !coordinatesIsNotValid && !isCoordinatesEmpty && !isCoordinatesValid && (
                    <RowStyle>
                        <StyledErrorIcon data={error_outlined} size={16} />
                        <StyledErrorTypography variant="body_short">{wrongCoordinates}</StyledErrorTypography>
                    </RowStyle>
                )}
                <RowStyleWithMany>
                    <StyledCheckbox
                        id="CoordinatesNotAvailable"
                        label="Sensor coordinates do not exist in Echo"
                        onChange={handleCoordinatesChange}
                        checked={selectedCoordinatesNotAvailable ? selectedCoordinatesNotAvailable : false}
                        disabled={isSaving}
                    />
                </RowStyleWithMany>
                <Wrapper>
                    <RowStyle>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <InputStyledLabel style={{ marginLeft: '8px' }}>Images</InputStyledLabel>
                        </div>
                        <InputStyledLabel style={{ marginLeft: 'auto', marginRight: '16px' }}>
                            Optional
                        </InputStyledLabel>
                    </RowStyle>
                    <RowStyle>
                        <ImagesButtonContainer style={{ width: '100%' }}>
                            <Button as="button" variant="outlined" onClick={() => setShowSelectFromLibrary(true)}>
                                <Icon data={image_add} size={24} />
                                Upload image
                            </Button>

                            <Button as="button" onClick={() => setShowCamera(true)}>
                                <Icon data={camera} size={24} />
                                Capture Photo
                            </Button>
                        </ImagesButtonContainer>
                    </RowStyle>

                    {files.length > 0 && (
                        <>
                            <ThumbnailsList images={paginatedData} />
                            {files.length > itemsPerPage && (
                                <PaginationContainer>
                                    <Pagination
                                        totalItems={files.length}
                                        itemsPerPage={itemsPerPage}
                                        defaultPage={currentPageIndex}
                                        onChange={(e, page) => setCurrentPageIndex(page)}
                                        withItemIndicator={false}
                                    />
                                </PaginationContainer>
                            )}
                        </>
                    )}
                </Wrapper>
                <StyledTextArea
                    label={
                        <RowStyle>
                            <InputStyledLabel>Additional description</InputStyledLabel>
                            <div ref={descriptionRef}>
                                <Button
                                    variant="ghost_icon"
                                    aria-label="save action"
                                    onClick={() => setTooltipDescriptionIsOpen(!isTooltipDescriptionOpen)}
                                >
                                    <Icon data={help_outline} size={24}></Icon>
                                </Button>
                            </div>
                        </RowStyle>
                    }
                    meta={
                        <RowStyle>
                            <InputStyledLabel>Optional</InputStyledLabel>
                        </RowStyle>
                    }
                    id="UserAddedDescription"
                    value={selectedDescription ? selectedDescription : ''}
                    onChange={handleChangeDescription}
                    required
                    rows={3}
                    maxLength={150}
                    placeholder={'Add description'}
                    disabled={isSaving}
                />
                <PopoverDialog
                    open={isTooltipDescriptionOpen}
                    anchorEl={descriptionRef.current}
                    onClose={() => setTooltipDescriptionIsOpen(false)}
                    title={'Additional description'}
                    content={tooltipDescription}
                />
                <PopoverDialog
                    open={isTooltipVendorSerialNumberOpen}
                    anchorEl={vendorSerialNumberRef.current}
                    onClose={() => setTooltipVendorSerialNumberIsOpen(false)}
                    title={'Vendor serial number'}
                    content={tooltipVendorSerialNumber}
                />
                <PopoverDialog
                    open={isTooltipSuperiorTagOpen}
                    anchorEl={superiorTagRef.current}
                    onClose={() => setTooltipSuperiorTagIsOpen(false)}
                    title={'Superior tag'}
                    content={tooltipVendorSerialNumber}
                />
                <PopoverDialog
                    open={isTooltipAreaOpen}
                    anchorEl={areaRef.current}
                    onClose={() => setTooltipAreaIsOpen(false)}
                    title={'Area'}
                    content={tooltipArea}
                />
                {editImageOpen &&
                    selectedFile &&
                    selectedImage?.file?.url &&
                    selectedFile.title &&
                    selectedFile.docNo && (
                        <EditImage
                            tag={sensor?.tag ? sensor?.tag : ''}
                            plant={sensor?.plant ? sensor?.plant : ''}
                            showImage={editImageOpen}
                            onClose={() => setEditImageOpen(false)}
                            url={selectedImage.file?.url}
                            description={selectedFile.title}
                            docNo={selectedFile.docNo}
                        />
                    )}
                {showCamera && (
                    <TakePhoto
                        plant={sensor?.plant ? sensor?.plant : ''}
                        tag={sensor?.tag ? sensor?.tag : ''}
                        showImage={showCamera}
                        onClose={() => setShowCamera(false)}
                    />
                )}

                {showSelectFromLibrary && (
                    <SelectImageFromLibrary
                        plant={sensor?.plant ? sensor?.plant : ''}
                        tag={sensor?.tag ? sensor?.tag : ''}
                        showImage={showSelectFromLibrary}
                        onClose={() => setShowSelectFromLibrary(false)}
                    />
                )}
            </StyledContainer>
            <RowContainerBottom>
                <Button as="button" onClick={handleCancelButton} variant="outlined" disabled={isSaving}>
                    <Label>Cancel</Label>
                </Button>
                <Button as="button" onClick={handleEditOnboardedSaveButton} disabled={isSaving}>
                    {isSaving ? <Progress.Dots /> : <StepperStyledActionLabel>Save</StepperStyledActionLabel>}
                </Button>
            </RowContainerBottom>
        </>
    )
}

const StyledAutocomplete = styled(Autocomplete)`
    gap: 16px;
    text-decoration: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    color: white;
    margin-bottom: 0px;
    background: none;

    @media (max-width: 1024px) {
        input:not(:disabled) {
            background-color: white;
        }
    }
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
    background: #ffffff;
    @media (max-width: 1024px) {
        background: none;
    }
    > * {
        margin-right: 0.3rem;
    }
`

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;
    color: white !important;
    background: #ffffff;
    @media (max-width: 1024px) {
        background: none;
    }
`

const InputStyledLabel = styled(Typography)`
    ${baseFont}
    color: #6f6f6f;
`

const StyledTextField = styled(TextField)`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    margin-bottom: 0px;
    background: none;

    @media (max-width: 1024px) {
        & > div {
            background: white;
            background-color: white;
        }
    }
`

const StyledTextArea = styled(Textarea)`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    margin-bottom: 0px;
    background: none;

    @media (max-width: 1024px) {
        & > div {
            background: white;
            background-color: white;
        }
    }
`

const StyledHelpIcon = styled(Icon)``

const RowStyleWithMany = styled(RowStyle)`
    justify-content: space-between;
    align-items: last baseline;
    margin-bottom: 4px;
`

const FormStyledLinkLabel = styled.span`
    ${baseFont}
    align-items: center;
    padding: 1px;
`

const StyledCheckbox = styled(Checkbox)`
    ${baseFont}
    color: white !important;

    @media (max-width: 1024px) {
        .cPrNYr {
            color: white !important;
        }
    }
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 8px 0;
    margin-bottom: 0px;
    gap: 16px;
`

const ImagesButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 0px;
`

const StepperStyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
    &:hover {
        color: white;
        background: #007079;
    }
    @media (max-width: 1024px) {
        color: white;
        &:hover {
            color: white;
            background: #007079;
        }
    }
`

const Label = styled.span`
    ${baseFont}
    @media (max-width: 1024px) {
        &:hover {
            background: #243746;
            color: white;
        }
    }
`

const StyledErrorIcon = styled(Icon)`
    title="Error";
    marginTop: 0;
    color: #b30d2f;
`

const StyledErrorTypography = styled(Typography)`
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    flex-shrink: 0;
    min-width: 0;
    flex: 1;
    color: #b30d2f;
`

const Thumbnail = styled.img`
    width: 100px;
    height: 150px;
    margin-bottom: 4px;

    border-radius: 5px;
    cursor: pointer;
`

const PaginationContainer = styled.div`
    display: flex;
    margin: 16px 0 0 0;
    justify-content: center;
`

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
`

export default EditOnboarded
